import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { Database, Download, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  exportDayCacheAsCSV,
  importDayCacheFromCSV,
} from "@/lib/persist/day-cache";
import { clearPersistedCache } from "@/lib/persist/query-persister";
import { notify } from "@/lib/toast";

const CLEAR_ON_CLOSE_KEY = "gha_clear_on_close";

/**
 * Reads the "auto-clear on tab close" preference from sessionStorage.
 * Defaulted to false so the resumable cache actually works out of the box;
 * flipping to true is the shared-browser / privacy-strict mode.
 */
function readClearOnClose(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(CLEAR_ON_CLOSE_KEY) === "1";
}

function writeClearOnClose(enabled: boolean): void {
  if (typeof sessionStorage === "undefined") return;
  if (enabled) {
    sessionStorage.setItem(CLEAR_ON_CLOSE_KEY, "1");
  } else {
    sessionStorage.removeItem(CLEAR_ON_CLOSE_KEY);
  }
}

function triggerCsvDownload(csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = `gha-day-cache-${new Date().toISOString().slice(0, 10)}.csv`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Cache control menu: explicit "Clear now" button, a session-scoped
 * preference for wiping the cache when the tab is closed, and CSV
 * export/import of the per-day slice cache.
 *
 * The pagehide listener lives here (close to its UI toggle) rather than at
 * the App level: registering / unregistering it follows the component
 * lifecycle, so HMR doesn't double-bind the event.
 */
export function CacheMenu() {
  const [clearOnClose, setClearOnClose] = useState<boolean>(() => readClearOnClose());
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Persist the preference whenever it flips.
  useEffect(() => {
    writeClearOnClose(clearOnClose);
  }, [clearOnClose]);

  // pagehide is more reliable than beforeunload — it fires on tab close,
  // navigation away, and mobile-browser freezing. The IDB wipe is
  // fire-and-forget: browsers may kill the page before it resolves, but
  // the operation starts immediately and usually completes on desktop.
  useEffect(() => {
    if (!clearOnClose) return;
    function handlePageHide() {
      void clearPersistedCache();
    }
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [clearOnClose]);

  const handleClearNow = useCallback(async () => {
    setClearing(true);
    try {
      await clearPersistedCache();
      notify.info("Local cache cleared", {
        description: "Query cache and per-day entries were wiped from this browser.",
        dedupeKey: "cache-cleared",
      });
    } finally {
      setClearing(false);
      setOpen(false);
    }
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const csv = await exportDayCacheAsCSV();
      triggerCsvDownload(csv);
      notify.info("Cache exported", {
        description: "CSV downloaded. Import it later to restore the dashboard data.",
        dedupeKey: "cache-exported",
      });
    } catch (err) {
      notify.error("Export failed", {
        description: err instanceof Error ? err.message : "Unknown error",
        dedupeKey: "cache-export-failed",
      });
    } finally {
      setExporting(false);
    }
  }, []);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so re-importing the same file fires a change event.
    e.target.value = "";
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const result = await importDayCacheFromCSV(text);
      const skippedSuffix = result.skipped > 0 ? ` (${result.skipped} rows skipped)` : "";
      const reposSuffix =
        result.reposAdded > 0
          ? ` · ${result.reposAdded} ${result.reposAdded === 1 ? "repo" : "repos"} added to recents`
          : "";
      notify.info(
        `Imported ${result.imported.toLocaleString()} runs across ${result.days} days${skippedSuffix}${reposSuffix}`,
        {
          description: "Open Pick a repo to see them in the recent list.",
          dedupeKey: "cache-imported",
        },
      );
    } catch (err) {
      notify.error("Import failed", {
        description: err instanceof Error ? err.message : "Invalid CSV",
        dedupeKey: "cache-import-failed",
      });
    } finally {
      setImporting(false);
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Manage local cache"
          title="Manage local cache"
        >
          <Database className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Local cache</DialogTitle>
          <DialogDescription>
            GitHub Actions data is cached in this browser (IndexedDB) so
            reloading is instant and interrupted fetches can resume. The cache
            is per-origin, never leaves your device, and is always wiped on
            sign out.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div className="flex-1">
              <div className="text-sm font-medium">Clear cache when I close this tab</div>
              <p className="mt-1 text-xs text-muted-foreground">
                Recommended on shared machines. Disables the resume-on-reload
                optimisation but guarantees no analysis data remains after you
                close the tab.
              </p>
            </div>
            <Switch
              checked={clearOnClose}
              onCheckedChange={setClearOnClose}
              aria-label="Clear cache on tab close"
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="flex-1">
              <div className="text-sm font-medium">Clear cache now</div>
              <p className="mt-1 text-xs text-muted-foreground">
                Wipes both the query cache and the per-day slice cache from
                this browser. Next fetch will re-download everything.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearNow}
              disabled={clearing}
            >
              <Trash2 className="size-4 mr-2" />
              {clearing ? "Clearing…" : "Clear"}
            </Button>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div className="flex-1">
              <div className="text-sm font-medium">Export / import (CSV)</div>
              <p className="mt-1 text-xs text-muted-foreground">
                Back up the per-day slice cache as a CSV to transfer to
                another browser or share a snapshot. Exports keep the
                fields the dashboard needs (run ids, timings, branches,
                conclusions) — not the full Octokit payload.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
              >
                <Download className="size-4 mr-2" />
                {exporting ? "Exporting…" : "Export"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleImportClick}
                disabled={importing}
              >
                <Upload className="size-4 mr-2" />
                {importing ? "Importing…" : "Import"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
